import { createContext, useContext } from 'react'
import { localizarPontoNoMundo, type CenaTabletop, type ObjetoTabletop, type ReferenciaPonto, type TipoObjetoTabletop, type VetorTabletop } from '../../dominio/tabletop'

/**
 * O que os equipamentos desenhados precisam saber da cena, sem que cada peça
 * receba a cena inteira: o eixo de cada mosquetão (no referencial dele), o
 * fator de destaque dos equipamentos pequenos e o giro das roldanas na puxada.
 */
export interface EquipamentosNaCena {
  /** Eixo maior de cada mosquetão, no referencial da peça (unitário). */
  readonly eixosDosMosquetoes: ReadonlyMap<string, VetorTabletop>
  /** Quanto o desenho de cada peça cresce no modo de destaque (1: tamanho real). */
  readonly fatorDaPeca: (idObjeto: string) => number
  /** Velocidade de giro de cada roldana na animação da puxada, em rad/s (sinal: sentido); chave `idObjeto:roldana`. */
  readonly giroDasRoldanas: ReadonlyMap<string, number>
  /** Fator das conexões nas pontas das cordas (anel, mosquetão, nó) no modo de destaque. */
  readonly fatorDasConexoes: number
  /** A puxada está animada: as cordas com sentido correm. */
  readonly animarPuxada: boolean
}

export const EQUIPAMENTOS_PADRAO: EquipamentosNaCena = {
  eixosDosMosquetoes: new Map(),
  fatorDaPeca: () => 1,
  giroDasRoldanas: new Map(),
  fatorDasConexoes: 1,
  animarPuxada: false,
}

/** Quanto os equipamentos pequenos crescem no modo de destaque. */
export const FATOR_DESTAQUE = 2.5
/** Os equipamentos de poucos centímetros, que somem numa cena de dezenas de metros. */
export const TIPOS_EM_DESTAQUE: ReadonlySet<TipoObjetoTabletop> = new Set([
  'polia',
  'poliaDupla',
  'mosquetao',
  'bloqueador',
  'noOito',
  'noFiel',
  'pontoAncoragem',
  'placaAncoragem',
  'descensor',
])

/**
 * O ponto de ancoragem no mundo com a peça aumentada em volta da origem dela:
 * `origem + fator · (ponto − origem)`, o mesmo que escalar o desenho.
 */
export function localizarComDestaque(
  cena: CenaTabletop,
  referencia: ReferenciaPonto,
  fatorDaPeca: (idObjeto: string) => number,
): VetorTabletop | null {
  const p = localizarPontoNoMundo(cena, referencia)
  const objeto: ObjetoTabletop | undefined = cena.objetos.find((o) => o.id === referencia.idObjeto)
  const f = fatorDaPeca(referencia.idObjeto)
  if (!p || !objeto || f === 1) return p
  const [ox, oy, oz] = objeto.transformacao.posicao
  return [ox + f * (p[0] - ox), oy + f * (p[1] - oy), oz + f * (p[2] - oz)]
}

export const ContextoEquipamentos = createContext<EquipamentosNaCena>(EQUIPAMENTOS_PADRAO)

export const useEquipamentosNaCena = () => useContext(ContextoEquipamentos)
